import { Link } from 'react-router-dom';
import { useBranding, resolveImageUrl } from '@/contexts/BrandingContext';
import { Building2, Phone, Mail, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function TermsPage() {
  const { branding } = useBranding();
  const terms = branding?.terms_conditions || {};
  const today = new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });

  const defaultContent = `Última actualización: ${today}

Bienvenido a ${branding?.site_name || 'Estadía 365'}. Estos Términos y Condiciones regulan el uso de nuestro sitio web, la información publicada, las consultas realizadas por usuarios, los enlaces a canales de reserva y la relación general entre ${branding?.site_name || 'Estadía 365'}, huéspedes, propietarios, hoteles y alojamientos asociados.

Al navegar por este sitio, consultar alojamientos, enviar formularios, comunicarse con nosotros o avanzar con una reserva, el usuario declara haber leído, comprendido y aceptado estos Términos y Condiciones.

1. Quiénes somos

${branding?.site_name || 'Estadía 365'} es una marca especializada en alojamientos temporarios, vacacionales y turísticos. Trabajamos con hoteles, cabañas, departamentos, casas privadas y otros alojamientos en Paraguay, Argentina y Brasil.

${branding?.site_name || 'Estadía 365'} puede actuar como gestor, intermediario, canal de comercialización, anfitrión o colaborador comercial de alojamientos, según el acuerdo establecido con cada propietario, hotel o responsable del alojamiento.

2. Uso del sitio web

El sitio web tiene como finalidad brindar información sobre alojamientos, destinos, servicios, disponibilidad orientativa, formas de contacto y canales de reserva. El usuario se compromete a utilizar el sitio de forma correcta, lícita y respetuosa.

3. Información de los alojamientos

Procuramos que la información publicada sea clara, actualizada y representativa. Sin embargo, ciertos datos pueden variar: disponibilidad, precios, servicios incluidos, reglas internas, políticas de cancelación, promociones, impuestos o cargos adicionales.

4. Disponibilidad

La disponibilidad está sujeta a confirmación. El envío de una consulta no garantiza la reserva ni bloquea fechas automáticamente.

5. Reservas

Las reservas pueden gestiónarse a través de distintos canales, incluyendo plataformas externas, enlaces de reserva, WhatsApp, formularios o canales propios del alojamiento. La reserva se considera confirmada únicamente cuando el canal correspondiente indique la confirmación.

6. Pagos

Los pagos se realizan a través del canal indicado para cada reserva. No solicitamos pagos por fuera del proceso informado. Las tarifas pueden estar expresadas en distintas monedas (USD, PYG, ARS, BRL).

7. Cancelaciónes, modificaciones y reembolsos

Las políticas dependen de cada alojamiento y del canal de reserva. Antes de confirmar, revise fechas límite, posibles cargos, tarifas no reembolsables y condiciones de modificación.

8. Check-in, check-out y reglas del alojamiento

Cada alojamiento tiene horarios, requisitos y reglas propias. El incumplimiento puede generar cargos adicionales.

9. Ubicación del alojamiento

Por seguridad, la ubicación exacta puede mostrarse después de confirmar la reserva.

10. Responsabilidad del huésped

El huésped es responsable de brindar información verdadera, respetar las reglas del alojamiento, cuidar la propiedad y responder por daños cuando corresponda.

11. Responsabilidad de propietarios

Los propietarios son responsables de la veracidad de la información, estado del alojamiento, habilitaciones, condiciones de seguridad y cumplimiento de reservas.

12. Rol de ${branding?.site_name || 'Estadía 365'}

Trabajamos para facilitar la conexión entre huéspedes y alojamientos, mejorar la visibilidad, acompañar consultas y colaborar en el proceso de reserva.

13. Enlaces a plataformas externas

El sitio puede incluir enlaces hacia plataformas externas. Al acceder, el usuario acepta los términos de ese sitio.

14. Imágenes y contenido

Las imágenes son informativas y representativas. El uso no autorizado del material está prohibido.

15. Tarifas y promociones

Las tarifas pueden ser orientativas y estar sujetas a cambios según temporada, disponibilidad y canal de reserva.

16. Comunicaciones

El usuario acepta que podamos contactarlo por WhatsApp, email, teléfono o redes sociales para gestiónar consultas y reservas.

17. Datos personales

Los datos se utilizan para gestiónar solicitudes y mejorar la experiencia. No vendemos información personal a terceros.

18. Limitación de responsabilidad

Realizamos esfuerzos razonables para brindar información clara y opciones confiables, sin garantizar disponibilidad permanente ni ausencia de inconvenientes.

19. Propietarios interesados

La incorporación de alojamientos puede requerir evaluación previa y acuerdo comercial. Nos reservamos el derecho de aceptar o rechazar alojamientos.

20. Modificación de estos términos

Podemos actualizar estos Términos en cualquier momento. La versión vigente será la publicada en el sitio.

21. Contacto

WhatsApp: ${branding?.contact?.phone || ''}
Email: ${branding?.contact?.email || ''}
Instagram: @estadia365

22. Ley aplicable

Estos Términos se interpretarán conforme a la legislación aplicable según el domicilio comercial y la jurisdicción correspondiente.`;

  const content = terms.content || defaultContent;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--brand-background, #F5F4F0)', fontFamily: 'var(--font-body, Inter)' }}>
      {/* Navbar */}
      <nav className="bg-white border-b border-[var(--brand-border,#E7E5E4)] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            {branding?.logo_url ? (
              <img src={resolveImageUrl(branding.logo_url)} alt={branding?.site_name} className="w-10 h-10 rounded-lg object-contain" />
            ) : (
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--brand-primary, #264639)' }}>
                <Building2 className="w-6 h-6 text-white" />
              </div>
            )}
            <span className="font-bold text-xl" style={{ fontFamily: 'var(--font-heading, Outfit)', color: 'var(--brand-text-primary, #1C1917)' }}>
              {branding?.site_name || 'Estadía 365'}
            </span>
          </Link>
          <Link to="/" className="text-sm font-medium hover:opacity-80" style={{ color: 'var(--brand-text-secondary, #57534E)' }}>
            <ArrowLeft className="w-4 h-4 inline mr-1" /> Volver
          </Link>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'var(--font-heading, Outfit)', color: 'var(--brand-text-primary, #1C1917)' }} data-testid="terms-title">
          {terms.title || 'Términos y Condiciones'}
        </h1>
        <div className="bg-white rounded-xl shadow-sm p-8 mt-6" data-testid="terms-content">
          <div className="prose prose-sm max-w-none whitespace-pre-line leading-relaxed" style={{ color: 'var(--brand-text-secondary, #57534E)' }}>
            {content}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#1C1917] py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-[#78716C] text-xs">{branding?.footer_text || `© ${new Date().getFullYear()} Estadía 365. Todos los derechos reservados.`}</p>
        </div>
      </footer>
    </div>
  );
}
